// @flow
import { getJson, postJson } from 'utils/http';
import { LISTING_TYPE_URL_SEGMENT_MAP } from 'utils/constants';
import type { Org as AdminOrg, GradProgram } from 'api/orgAdmin';
import type { ImageData, ImageWithTextData } from 'api/shared';
import type { Volop } from 'api/volops';

export type Address = {
  locationHidden?: boolean,
  line1: ?string,
  line2: ?string,
  city: string,
  state: string,
  stateCode: string,
  zipcode: ?string,
  country: string,
  latitude: number,
  longitude: number,
  description: ?string
};

export type ListingMini = {
  id: string,
  type: string,
  name: string,
  url: { [string]: string },
  firstPublished: string,
  address: {
    city: string,
    stateCode: string,
    country: string
  },
  isVirtual: boolean
};

export type Org = {
  id: string,
  created: string,
  name: string,
  orgType: string,
  description: string,
  descriptionTruncated: string,
  email: string,
  phone: string,
  links: Array<string>,
  address: Address,
  url: { [string]: string },
  areasOfFocus: Array<string>,
  areasOfFocusKeys: Array<string>,
  jobs: Array<ListingMini>,
  internships: Array<ListingMini>,
  volops: Array<ListingMini>,
  idealistDayEvents: Array<ListingMini>,
  banner: ImageData,
  logo: ImageData,
  images: Array<ImageWithTextData>,
  isFrozen?: boolean,
  isPublished?: boolean,
  is501c3?: boolean,
  hasAts: boolean
};

export type OrgMini = {
  id: string,
  name: string,
  orgType: string,
  address: {
    city: string,
    stateCode: ?string,
    country: string
  },
  url: { [string]: string },
  logo: ?ImageData
};

export type Job = {|
  id: string,
  type: 'JOB',
  name: string,
  org: OrgMini,
  hasOrgNameOverride: boolean,
  orgNameOverride: ?string,
  description: string,
  descriptionTruncated: string,
  address: Address,
  hasAts: boolean,
  requestedAttachments: Array<{ label: string, isRequired: boolean }>,
  applyEmail: ?string,
  applyUrl: ?string,
  applyText: ?string,
  applyTextTruncated: ?string,
  benefits: ?string,
  benefitsTruncated: ?string,
  salaryMinimum: ?number,
  salaryMaximum: ?number,
  salaryDetails: ?string,
  professionalLevel: string,
  languageProficiency: ?string,
  languageProficiencyTruncated: ?string,
  education: string,
  applicationDeadline: ?string,
  startDate: ?string,
  isFullTime: boolean,
  isTemporary: boolean,
  isContract: boolean,
  remoteOk: boolean,
  url: { [string]: string },
  firstPublished: string
|};

export type Internship = {|
  id: string,
  type: 'INTERNSHIP',
  name: string,
  org: OrgMini,
  hasOrgNameOverride: boolean,
  orgNameOverride: ?string,
  description: string,
  descriptionTruncated: string,
  address: Address,
  paymentOk: boolean,
  hasAts: boolean,
  requestedAttachments: Array<{ label: string, isRequired: boolean }>,
  applyEmail: ?string,
  applyUrl: ?string,
  applyText: ?string,
  applyTextTruncated: ?string,
  academicCredits: ?boolean,
  benefits: ?string,
  benefitsTruncated: ?string,
  paid: boolean,
  payment: ?string,
  wage: ?string,
  remoteOk: boolean,
  hoursPerWeek: ?number,
  applicationDeadline: ?string,
  startDate: ?string,
  endDate: ?string,
  url: { [string]: string },
  firstPublished: string
|};

export type Listing = Job | Internship | Volop;

export type AreasOfFocus = Array<{
  id: string,
  text: string,
  usageRanking: string
}>;

export type JobFunctions = Array<{
  id: string,
  text: string,
  usageRanking: string
}>;

export type InternshipFunctions = Array<{
  id: string,
  text: string,
  usageRanking: string
}>;

export type VolopFunctions = Array<{
  id: string,
  text: string,
  usageRanking: string
}>;

export function createOrg(orgInfo: Object): Promise<AdminOrg> {
  return postJson(`/data/website/organizations`, orgInfo).then(
    ({ org }) => org
  );
}

export function fetchOrg(orgId: string): Promise<Org> {
  return getJson(`/data/website/organizations/${orgId}`).then(({ org }) => org);
}

export function requestAdmin(orgId: string, requestTo: string) {
  return postJson(`/data/website/organizations/${orgId}/request-admin`, {
    requestTo
  });
}

export function fetchListing(
  listingType: string,
  listingId: string
): Promise<Listing> {
  return getJson(
    `/data/website/${LISTING_TYPE_URL_SEGMENT_MAP[listingType]}/${listingId}`
  ).then(
    ({ job, internship, volop, event }) => job || internship || volop || event
  );
}

export function fetchAreasOfFocus(): Promise<AreasOfFocus> {
  return getJson(`/data/website/areas-of-focus?lang=${USER_LOCALE}`).then(
    ({ areasOfFocus }) => areasOfFocus
  );
}

export function fetchJobFunctions(): Promise<JobFunctions> {
  return getJson(`/data/website/job-functions?lang=${USER_LOCALE}`).then(
    ({ jobFunctions }) => jobFunctions
  );
}

export function fetchInternshipFunctions(): Promise<JobFunctions> {
  return getJson(`/data/website/internship-functions?lang=${USER_LOCALE}`).then(
    ({ internshipFunctions }) => internshipFunctions
  );
}

export function fetchVolopFunctions(): Promise<VolopFunctions> {
  return getJson(`/data/website/volop-functions?lang=${USER_LOCALE}`).then(
    ({ volopFunctions }) => volopFunctions
  );
}

export function createOrgWithGradProgram(
  info: Object
): Promise<{ org: AdminOrg, gradProgram: GradProgram }> {
  return postJson('/data/website/organizations-with-grad-program', info);
}
